'use client'
import React from 'react'
import { useState } from 'react'
import Link from '../../components/framework/link'
import { ChevronDown } from 'lucide-react'

import type { FooterGroup } from '../../config/site'

/**
 * The footer's navigation groups as accordions, on phones only.
 *
 * The only client component in the footer, and the only reason any of it ships
 * JavaScript. Groups open independently and several can be open at once —
 * there is no coordination worth the state to manage it.
 *
 * The panel is always rendered and collapsed with a grid-rows transition
 * (`0fr` → `1fr`), which animates to the content's natural height without
 * anyone measuring it. `hidden` on the inner wrapper keeps collapsed links out
 * of the tab order and the accessibility tree, so the transition never costs
 * correctness.
 */
export function MobileFooterNav({ groups }: { groups: readonly FooterGroup[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({})

  return (
    <nav aria-label="Footer" className="lg:hidden">
      <ul className="divide-y divide-white/10 border-y border-white/10">
        {groups.map((group) => {
          const isOpen = Boolean(open[group.id])
          const panelId = `footer-panel-${group.id}`

          return (
            <li key={group.id}>
              <h3>
                <button
                  type="button"
                  onClick={() => setOpen((s) => ({ ...s, [group.id]: !s[group.id] }))}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="focus-ring-light flex min-h-14 w-full items-center justify-between gap-4 py-2 text-left text-[0.9375rem] font-semibold text-white"
                >
                  {group.heading}
                  <ChevronDown
                    className={`size-4 shrink-0 text-white/50 motion-arrow ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </h3>

              <div
                id={panelId}
                className={`grid transition-all duration-300 ease-out motion-reduce:transition-none ${
                  isOpen ? 'grid-rows-[1fr] pb-4 opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <ul hidden={!isOpen} className="space-y-1">
                    {group.links.map((link) => (
                      <li key={link.href}>
                        <Link
                          href={link.href}
                          className="focus-ring-light block py-2 text-[0.9375rem] text-white/65 motion-micro hover:text-white"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
